<script setup lang="ts">
import type { Themeable } from '#theme'
import { pick } from 'es-toolkit'
import type { NuxtLinkProps } from '#app'
import { NuxtLink, UIIcon } from '#components'
import { computed, toCSSUnit, useDefaults, useTheme } from '#imports'

export interface UIListLinkProps extends NuxtLinkProps, Themeable {
    height?: string | number
    prependIcon?: string
    appendIcon?: string
}

const _props = withDefaults(defineProps<UIListLinkProps>(), {
    prependIcon: undefined,
    appendIcon: undefined,
    color: undefined
})
const props = useDefaults({
    props: _props,
    defaults: {
        height: 40,
        color: 'secondary'
    }
})

const { themeClass } = useTheme(() => {
    return {
        color: props.color
    }
})

// To avoid all possible props in NuxtLinkProps to be set on component.
const allowedKeys = ['as', 'prepenbdIcon', 'appendIcon', 'height', 'to', 'href', 'external']

const slots = defineSlots<{
    prependIcon?(): any
    prepend?(): any
    default(): any
    append?(): any
    appendIcon?(): any
}>()

const styles = computed(() => {
    return {
        height: toCSSUnit(props.height),
    }
})

const gridTemplateColumns = computed(() => {
    let value = ''

    if (slots.prepend) {
        value += 'auto '
    }

    // Default slot
    value += '1fr'

    if (slots.append) {
        value += ' auto'
    }

    return value
})
</script>

<template>
    <li class="list-link__wrap" :class="themeClass">
        <NuxtLink v-bind="pick(props, allowedKeys)" class="list-link">
            <slot v-if="slots.prependIcon || props.prependIcon" name="prependIcon">
                <UIIcon v-if="props.prependIcon" :name="props.prependIcon" size="18" />
            </slot>

            <div class="list-link__content">
                <div v-if="slots.prepend" class="list-link__content-prepend">
                    <slot name="prepend" />
                </div>

                <div class="list-link__content-main">
                    <slot />
                </div>

                <div v-if="slots.append" class="list-link__content-append">
                    <slot name="append" />
                </div>
            </div>

            <slot v-if="slots.appendIcon || props.appendIcon" name="appendIcon">
                <UIIcon v-if="props.appendIcon" :name="props.appendIcon" size="18" />
            </slot>
        </NuxtLink>
    </li>
</template>

<style lang="scss" scoped>
a {
    text-decoration: none;
}

.list-link {
    font-size: $font-size-lg;
    font-weight: 500;
    display: flex;
    height: v-bind('styles.height');
    align-items: center;
    gap: 10px;
    text-decoration: none;
    width: 100%;
    color: var(--color);
    :deep(.icon) {
        color: var(--color);
    }

    &__wrap {
        width: 100%;
        list-style-type: none;
    }

    &__content {
        display: grid;
        grid-template-rows: 1fr;
        grid-template-columns: v-bind(gridTemplateColumns);
        justify-items: flex-start;
        gap: 16px;
        flex-grow: 1;

        &-prepend {
            justify-self: flex-start;
        }

        &-append {
            justify-self: flex-end;
        }
    }
}
</style>
